import { useEffect } from 'react'

import { clientLogExperiment } from 'experiments/clientExperimentService'
import { EXPERIMENT_NAME } from 'experiments/constants/Experiments'
import { useExperimentDispatch, useExperimentState } from 'experiments/hooks/useExperiment'
import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import { getMParticleSessionId } from 'mParticle/mParticleService'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import { useUser } from 'shared/hooks/useUser'

type Error = {
  message: string | null
}
/**
 * Logs the exposure of a user to a Darwin experiment.
 * @param experimentName Darwin experiment name
 * @param forceDisable If true, it forces the service to be disabled
 */
export const useLogExperiment = (
  experimentName: EXPERIMENT_NAME,
  forceDisable: boolean = false,
) => {
  const areExperimentsEnabled = useFeatureFlag(FeatureFlag.WEB_FC_ENABLE_EXPERIMENTS)
  const { currentUser, currentUserType } = useUser()
  const userId = currentUser?.id
  const sessionId = getMParticleSessionId()
  const enableExperiment = areExperimentsEnabled && !forceDisable
  const experimentDispatch = useExperimentDispatch()
  const experimentState = useExperimentState()
  const currentExperiment = experimentState?.[experimentName]
  const groupAssigned = currentExperiment?.groupAssigned
  const hasLoggedExperiment = currentExperiment?.hasLoggedExperiment

  useEffect(() => {
    if (!enableExperiment || !groupAssigned || hasLoggedExperiment) return

    const logExposure = async (metaData: Record<string, unknown> = {}) => {
      try {
        await clientLogExperiment(experimentName, {
          ...metaData,
          currentUserType,
          userId,
          sessionId,
        })
        experimentDispatch({ type: 'logExperiment', payload: { experimentName } })
      } catch (error) {
        experimentDispatch({
          type: 'setError',
          payload: { experimentName, error: (error as Error)?.message as string | null },
        })
      }
    }

    logExposure()
  }, [
    currentUserType,
    enableExperiment,
    experimentDispatch,
    experimentName,
    groupAssigned,
    hasLoggedExperiment,
    userId,
  ])
}
